// =============================================================================
// appUpdate — "Update app" for the phone (29 Sep 2026)
// =============================================================================
// James: "the guys save the URL as a screen app but on iPhones you can't
// refresh, so stale app — you need to remove, open Safari, refresh and save
// as home screen again. An in-app refresh button to call the latest would be
// way better."
//
// What goes stale: the service worker's precache (the app's JS/CSS). The PWA
// registers as autoUpdate, but a home-screen app on iOS can sit open for days
// and only checks for a new worker on a fresh launch. Two things fix it:
//   1. main.tsx asks the worker to check for updates every 30 minutes and
//      every time the app comes back to the foreground (autoUpdate then
//      swaps in the new build on its own).
//   2. refreshApp() — the button: unregister the worker, drop the caches,
//      reload from the network. Exactly what "remove and re-add" did, in one
//      tap. The offline queue lives in IndexedDB, not in these caches, so
//      nothing queued is lost. Sign-in is untouched.
// =============================================================================
declare const __BUILD_TIME__: string;

export const BUILD_TIME: string = typeof __BUILD_TIME__ === "string" ? __BUILD_TIME__ : "";

export function buildLabel(): string {
  if (!BUILD_TIME) return "";
  const d = new Date(BUILD_TIME);
  return isNaN(d.getTime()) ? BUILD_TIME : d.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export async function refreshApp(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister().catch(() => false)));
    }
  } catch { /* no worker — fine */ }
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k).catch(() => false)));
    }
  } catch { /* storage blocked — the reload still fetches fresh */ }
  // Cache-busting query so even a stubborn HTTP cache serves the new shell.
  const url = new URL(window.location.href);
  url.searchParams.set("_r", String(Date.now()));
  window.location.replace(url.toString());
}

// Ask the worker to look for a new build now (autoUpdate applies it).
export async function checkForUpdate(): Promise<void> {
  try {
    if (!("serviceWorker" in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    await reg?.update();
  } catch { /* offline — next time */ }
}
